import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { WithHtmlSavvyTitle } from '@/with-html-savvy-title';
import { useValidateMeetingLink } from '@/features/meeting/hooks/use-validate-meeting-link.query';
import { Loader } from '@/components/loader/loader';
import { PageNotFound } from '@/routes/page-not-found';
import { useAccountSwitchOnLinkValidation } from '@/features/account/hooks/use-account-switch-on-link-validation';
import { useAuthorization } from '@/auth/hooks/use-authorization';
import { useNavigateTo } from '@/routes/hooks/use-navigate-to';
import { useCurrentUser } from '@/user/use-current-user.query';
import { QueryKey } from '@/config/query-client';
import { Flexbox } from '@/ui/flexbox/flexbox';

export const MeetingWithLinkWrapperPage = () => {
  const { linkId } = useParams<{ linkId: string }>();
  const { t } = useTranslation('default');
  const { isUserAuthorized } = useAuthorization();
  const navigateTo = useNavigateTo();
  const location = useLocation();
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();

  const {
    data: validationData,
    isLoading,
    error,
  } = useValidateMeetingLink(linkId || '');

  const [shouldRedirectAfterSwitch, setShouldRedirectAfterSwitch] =
    useState(false);
  const redirectPathRef = useRef<string | null>(null);
  const previousAccountNameRef = useRef<string>('');
  const trackedLinkIdRef = useRef(linkId);

  if (trackedLinkIdRef.current !== linkId) {
    trackedLinkIdRef.current = linkId;
    redirectPathRef.current = null;
  }

  if (validationData?.isValid === false) {
    redirectPathRef.current = null;
  }

  useEffect(() => {
    redirectPathRef.current = null;
    setShouldRedirectAfterSwitch(false);

    if (linkId) {
      void queryClient.invalidateQueries({
        queryKey: [QueryKey.MeetingLink, 'validate', linkId],
      });
    }

    return () => {
      redirectPathRef.current = null;
      setShouldRedirectAfterSwitch(false);
    };
  }, [linkId, queryClient]);

  useEffect(() => {
    if (validationData?.redirectPath && !redirectPathRef.current) {
      redirectPathRef.current = validationData.redirectPath;
    }
  }, [validationData?.redirectPath]);

  const { handleAccountSwitch, isSwitching, shouldSwitch } =
    useAccountSwitchOnLinkValidation({
      validationData,
      onSwitchComplete: (previousAccountName: string) => {
        previousAccountNameRef.current = previousAccountName;
        setShouldRedirectAfterSwitch(true);
      },
    });

  const getRedirectUrlWithNotification = () => {
    if (!redirectPathRef.current || !validationData) {
      return redirectPathRef.current;
    }

    const url = new URL(redirectPathRef.current, window.location.origin);
    url.searchParams.set('accountSwitched', 'true');
    url.searchParams.set('accountName', validationData.accountName || '');
    url.searchParams.set('previousAccountName', previousAccountNameRef.current);
    url.searchParams.set('entityType', 'meeting');

    return url.pathname + url.search;
  };

  useEffect(() => {
    if (shouldSwitch && !shouldRedirectAfterSwitch) {
      handleAccountSwitch();
    }
  }, [shouldSwitch, handleAccountSwitch, shouldRedirectAfterSwitch]);

  useEffect(() => {
    if (shouldRedirectAfterSwitch && !isSwitching && redirectPathRef.current) {
      const redirectUrl = getRedirectUrlWithNotification();
      if (redirectUrl) {
        window.location.href = redirectUrl;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRedirectAfterSwitch, isSwitching]);

  const isLoadingState = isLoading || isSwitching;
  const isValidRedirect =
    validationData?.isValid &&
    validationData?.redirectPath &&
    validationData?.accountId &&
    currentUser.data &&
    currentUser.data.account.id === validationData.accountId;
  const shouldShowNotFound =
    !isLoading &&
    !isSwitching &&
    !shouldRedirectAfterSwitch &&
    (error ||
      !validationData?.isValid ||
      (!validationData?.redirectPath && !redirectPathRef.current));

  const loadingComponent = useMemo(
    () => (
      <WithHtmlSavvyTitle title={t`loading` || 'Loading...'}>
        <Flexbox justify="center" alignItems="center" height={`100vh`}>
          <Loader />
        </Flexbox>
      </WithHtmlSavvyTitle>
    ),
    [t],
  );

  if (isLoadingState) {
    return loadingComponent;
  }

  if (shouldShowNotFound) {
    return <PageNotFound />;
  }

  if ((isLoading || !validationData) && !redirectPathRef.current) {
    return loadingComponent;
  }

  if (!isUserAuthorized) {
    const currentPath = `${location.pathname}${location.search}`;
    const redirectUrl = `redirectTo=${encodeURIComponent(currentPath)}`;
    navigateTo.signIn(undefined, redirectUrl);
    return loadingComponent;
  }

  if (shouldRedirectAfterSwitch && !isSwitching && redirectPathRef.current) {
    return loadingComponent;
  }

  if (
    !shouldSwitch &&
    !isSwitching &&
    !shouldRedirectAfterSwitch &&
    isValidRedirect &&
    validationData.redirectPath
  ) {
    return <Navigate to={validationData.redirectPath} replace />;
  }

  return loadingComponent;
};
